// frontend/src/pages/configuracion/EtapasTratamiento.jsx
import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Grid, Button, IconButton, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Box, Tooltip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';

import { getAllEtapas, createEtapa, updateEtapa, deleteEtapa, reordenarEtapas } from '../../services/etapaTratamientoService';
import especialidadService from '../../services/especialidadService';
import programaService from '../../services/programaService';
import PageHeader from '../../components/common/PageHeader';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';

const EtapasTratamiento = () => {
  const [etapas, setEtapas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [selectedEspecialidad, setSelectedEspecialidad] = useState('');
  const [selectedPrograma, setSelectedPrograma] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentEtapa, setCurrentEtapa] = useState({
    nombre: '',
    especialidad_id: '',
    programa_id: '',
    duracion_estimada: '',
    descripcion: '',
    orden: 1
  });
  const [etapaToDelete, setEtapaToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [etapasResponse, especialidadesResponse, programasResponse] = await Promise.all([
          getAllEtapas(),
          especialidadService.getEspecialidades(),
          programaService.getProgramas()
        ]);
        setEtapas(etapasResponse.data);
        setEspecialidades(especialidadesResponse.data);
        setProgramas(programasResponse.data);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar los datos. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = (etapa = null) => {
    if (etapa) {
      setCurrentEtapa({
        id: etapa.id,
        nombre: etapa.nombre,
        especialidad_id: etapa.especialidad_id,
        programa_id: etapa.programa_id || '',
        duracion_estimada: etapa.duracion_estimada || '',
        descripcion: etapa.descripcion || '',
        orden: etapa.orden || 1
      });
    } else {
      setCurrentEtapa({
        nombre: '',
        especialidad_id: selectedEspecialidad || '',
        programa_id: selectedPrograma || '',
        duracion_estimada: '',
        descripcion: '',
        orden: getNextOrder()
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (etapa) => {
    setEtapaToDelete(etapa);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setEtapaToDelete(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentEtapa({ ...currentEtapa, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (currentEtapa.id) {
        await updateEtapa(currentEtapa.id, currentEtapa);
      } else {
        await createEtapa(currentEtapa);
      }
      
      // Recargar etapas
      const response = await getAllEtapas();
      setEtapas(response.data);
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar etapa:', error);
      setError('Error al guardar la etapa. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!etapaToDelete) return;
    
    try {
      setLoading(true);
      await deleteEtapa(etapaToDelete.id);
      
      // Recargar etapas
      const response = await getAllEtapas();
      setEtapas(response.data);
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error al eliminar etapa:', error);
      setError('Error al eliminar la etapa. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEspecialidadChange = (e) => {
    setSelectedEspecialidad(e.target.value);
    setSelectedPrograma('');
  };

  const handleProgramaChange = (e) => {
    setSelectedPrograma(e.target.value);
  };

  const getNextOrder = () => {
    const filteredEtapas = getFilteredEtapas();
    if (filteredEtapas.length === 0) return 1;
    
    const maxOrden = Math.max(...filteredEtapas.map(etapa => etapa.orden || 0));
    return maxOrden + 1;
  };

  const getFilteredEtapas = () => {
    return etapas.filter(etapa => {
      const matchesEspecialidad = !selectedEspecialidad || etapa.especialidad_id === parseInt(selectedEspecialidad);
      const matchesPrograma = !selectedPrograma || etapa.programa_id === parseInt(selectedPrograma);
      return matchesEspecialidad && matchesPrograma;
    }).sort((a, b) => a.orden - b.orden);
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const filteredEtapas = getFilteredEtapas();
    const newEtapas = [...filteredEtapas];
    
    // Intercambiar orden
    const temp = newEtapas[index].orden;
    newEtapas[index].orden = newEtapas[index - 1].orden;
    newEtapas[index - 1].orden = temp;
    
    // Enviar al servidor
    try {
      await reordenarEtapas(newEtapas.map(e => ({ id: e.id, orden: e.orden })));
      
      // Recargar etapas
      const response = await getAllEtapas();
      setEtapas(response.data);
    } catch (error) {
      console.error('Error al reordenar etapas:', error);
      setError('Error al reordenar las etapas. Por favor, intente nuevamente.');
    }
  };

  const handleMoveDown = async (index) => {
    const filteredEtapas = getFilteredEtapas();
    if (index === filteredEtapas.length - 1) return;
    
    const newEtapas = [...filteredEtapas];
    
    // Intercambiar orden
    const temp = newEtapas[index].orden;
    newEtapas[index].orden = newEtapas[index + 1].orden;
    newEtapas[index + 1].orden = temp;
    
    // Enviar al servidor
    try {
      await reordenarEtapas(newEtapas.map(e => ({ id: e.id, orden: e.orden })));
      
      // Recargar etapas
      const response = await getAllEtapas();
      setEtapas(response.data);
    } catch (error) {
      console.error('Error al reordenar etapas:', error);
      setError('Error al reordenar las etapas. Por favor, intente nuevamente.');
    }
  };

  return (
    <Container maxWidth="lg">
      <PageHeader title="Etapas de Tratamiento" />
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="especialidad-label">Especialidad</InputLabel>
                <Select
                  labelId="especialidad-label"
                  value={selectedEspecialidad}
                  onChange={handleEspecialidadChange}
                  label="Especialidad"
                >
                  <MenuItem value="">Todas las especialidades</MenuItem>
                  {especialidades.map(esp => (
                    <MenuItem key={esp.id} value={esp.id}>{esp.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="programa-label">Programa</InputLabel>
                <Select
                  labelId="programa-label"
                  value={selectedPrograma}
                  onChange={handleProgramaChange}
                  label="Programa"
                  disabled={!selectedEspecialidad}
                >
                  <MenuItem value="">Todos los programas</MenuItem>
                  {programas
                    .filter(prog => !selectedEspecialidad || prog.especialidad_id === parseInt(selectedEspecialidad))
                    .map(prog => (
                      <MenuItem key={prog.id} value={prog.id}>{prog.nombre}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Etapa
        </Button>
      </Box>
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Orden</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Especialidad</TableCell>
                <TableCell>Programa</TableCell>
                <TableCell>Duración Estimada</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredEtapas().map((etapa, index) => (
                <TableRow key={etapa.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {etapa.orden}
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveDown(index)}
                        disabled={index === getFilteredEtapas().length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{etapa.nombre}</TableCell>
                  <TableCell>
                    {especialidades.find(e => e.id === etapa.especialidad_id)?.nombre || '-'}
                  </TableCell>
                  <TableCell>
                    {programas.find(p => p.id === etapa.programa_id)?.nombre || 'No específico'}
                  </TableCell>
                  <TableCell>
                    {etapa.duracion_estimada ? `${etapa.duracion_estimada} sesión(es)` : 'Variable'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Editar">
                      <IconButton onClick={() => handleOpenDialog(etapa)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton onClick={() => handleOpenDeleteDialog(etapa)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {getFilteredEtapas().length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay etapas configuradas para los filtros seleccionados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Diálogo para crear/editar etapa */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentEtapa.id ? 'Editar Etapa' : 'Nueva Etapa'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="nombre"
                label="Nombre de la Etapa"
                value={currentEtapa.nombre}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Especialidad</InputLabel>
                <Select
                  name="especialidad_id"
                  value={currentEtapa.especialidad_id}
                  onChange={handleChange}
                  label="Especialidad"
                >
                  <MenuItem value="">Seleccione una especialidad</MenuItem>
                  {especialidades.map(esp => (
                    <MenuItem key={esp.id} value={esp.id}>{esp.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Programa</InputLabel>
                <Select
                  name="programa_id"
                  value={currentEtapa.programa_id}
                  onChange={handleChange}
                  label="Programa"
                >
                  <MenuItem value="">No específico</MenuItem>
                  {programas
                    .filter(prog => !currentEtapa.especialidad_id || prog.especialidad_id === parseInt(currentEtapa.especialidad_id))
                    .map(prog => (
                      <MenuItem key={prog.id} value={prog.id}>{prog.nombre}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="duracion_estimada"
                label="Duración Estimada (sesiones)"
                type="number"
                value={currentEtapa.duracion_estimada}
                onChange={handleChange}
                fullWidth
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="orden"
                label="Orden"
                type="number"
                value={currentEtapa.orden}
                onChange={handleChange}
                fullWidth
                required
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={currentEtapa.descripcion}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            color="primary"
            disabled={!currentEtapa.nombre || !currentEtapa.especialidad_id || loading}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmación para eliminar */}
      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
        title="Eliminar Etapa"
        content={`¿Está seguro que desea eliminar la etapa "${etapaToDelete?.nombre}"? Esta acción no se puede deshacer.`}
      />
    </Container>
  );
};

export default EtapasTratamiento;